/**
 * Observatoire (PRD §13, BACKLOG G1/G3) côté navigateur : file d'événements envoyée par `sendEvents` (ui/src/api.ts)
 * UNIQUEMENT après consentement explicite (case décochée par défaut, retrait à tout moment — docs/legal/confidentialite.md).
 *
 * Règles :
 * - sans consentement : `track` n'enregistre rien et `flush` n'envoie rien (aucune file en mémoire non plus) ;
 * - questions de croyance (`belief_before` / `belief_after`) : JAMAIS mises en file tant que le paramètre
 *   `observatory_beliefs_enabled` vaut 0 (en attente de relecture juridique, art. 9 RGPD : opinions politiques) ;
 * - pas d'horodatage client : seulement `seq` (rang dans la session) ; identifiant de session en sessionStorage.
 */
import { apiHealth, sendEvents, sessionId, type ApiHealth, type TelemetryBatch } from "./api.js";

const CONSENT_KEY = "bfg.observatoire.consent.v1";
const BELIEF_TYPES = new Set(["belief_before", "belief_after"]);
/** Taille max d'un lot (schéma : 200 événements) ; envoi anticipé au-delà de la moitié. */
const BATCH_MAX = 200;

export type TelemetryEvent = Record<string, unknown> & { type: string };

interface State {
  queue: Array<TelemetryEvent & { seq: number }>;
  seq: number;
  beliefsEnabled: boolean;
  /** Mémoire de session si localStorage est indisponible (navigation privée stricte). */
  memConsent: boolean;
}
const S: State = { queue: [], seq: 0, beliefsEnabled: false, memConsent: false };

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** Paramétrage à l'amorçage : `observatory_beliefs_enabled` (params) — 1 = questions de croyance autorisées. */
export function configureTelemetry(opts: { beliefsEnabled: boolean }): void {
  S.beliefsEnabled = opts.beliefsEnabled === true;
}

export function beliefsEnabled(): boolean {
  return S.beliefsEnabled;
}

export function hasConsent(): boolean {
  const s = storage();
  if (!s) return S.memConsent;
  try {
    return s.getItem(CONSENT_KEY) === "oui";
  } catch {
    return S.memConsent;
  }
}

/** Opt-in / retrait. Le retrait vide la file locale (rien de ce qui n'est pas parti ne partira). */
export function setConsent(on: boolean): void {
  S.memConsent = on;
  const s = storage();
  try {
    if (on) s?.setItem(CONSENT_KEY, "oui");
    else s?.removeItem(CONSENT_KEY);
  } catch {
    /* stockage indisponible : la mémoire de session suffit */
  }
  if (!on) S.queue = [];
}

/** Met un événement en file ; false s'il est refusé (pas de consentement, croyance désactivée). */
export function track(ev: TelemetryEvent): boolean {
  if (!hasConsent()) return false;
  if (BELIEF_TYPES.has(ev.type) && !S.beliefsEnabled) return false;
  S.queue.push({ ...ev, seq: S.seq++ });
  if (S.queue.length >= BATCH_MAX / 2) void flush();
  return true;
}

/** Événements en attente (tests, débogage). */
export function pending(): ReadonlyArray<TelemetryEvent & { seq: number }> {
  return S.queue;
}

/** Envoie la file (jamais bloquant, jamais d'exception). */
export async function flush(health: () => Promise<ApiHealth | null> = apiHealth, send: (b: TelemetryBatch) => Promise<boolean> = sendEvents): Promise<boolean> {
  if (!hasConsent() || S.queue.length === 0) return false;
  const h = await health();
  if (!h?.telemetry || !h.versions) return false;
  // Garde finale : même si un appelant a contourné `track`, aucune croyance ne part tant que le paramètre vaut 0.
  const events = S.queue.splice(0, BATCH_MAX).filter((e) => S.beliefsEnabled || !BELIEF_TYPES.has(e.type));
  if (!events.length || !hasConsent()) return false;
  const batch: TelemetryBatch = {
    session_id: sessionId(),
    consent: true,
    engine_version: h.versions.engine,
    params_version: h.versions.params,
    content_version: h.versions.content,
    events,
  };
  return send(batch);
}

/** Réinitialisation (tests). */
export function _resetTelemetry(): void {
  S.queue = [];
  S.seq = 0;
  S.beliefsEnabled = false;
  S.memConsent = false;
}
